import IconButton from 'components/base/icon-button';
import { ReactComponent as TrashIcon } from 'assets/icons/trash-icon.svg';
import { ReactComponent as MinusIcon } from 'assets/icons/minus-icon.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus-icon.svg';
import React, { useEffect, useState } from 'react';
import { Dropdown } from '@cokitchen/cokitchen-components';
import { ArrowDownIcon, CloseOutlineIcon, InfoIcon, Users2Icon } from 'cokitchen-icons';
import { formatCurrency } from 'utils/functions';
import './styles.scss';
import AssignMealModal from 'components/modals/assign-meal';
import useOrdersContext from 'contexts/orders.context';
import BulkOrderService from 'services/bulk-order.service';
import { ENUM_ORDERS_TYPE } from 'utils/enum';
import { OrderMealType } from 'models/order.model';

type Props = {
  meal: OrderMealType;
  removeMeal?: (arg0: OrderMealType) => void;
  type: 'bulk' | 'group' | 'batch';
  onClick?: () => void;
  price?: number;
};
const MealItem = ({ onClick, meal, removeMeal, type, price = meal?.price }: Props) => {
  const [showQuantityDropdown, setShowQuantityDropdown] = useState(false);
  const [showAssignMealModal, setShowAssignMealModal] = useState(false);
  const [usersExceedMeals, setUsersExceedMeals] = useState<boolean>(false);
  const { setBulkOrderMeals, bulkOrderMeals, batchOrderMeals, setBatchOrderMeals } =
    useOrdersContext();
  useEffect(() => {
    if (meal.members) {
      setUsersExceedMeals(meal?.members?.length > meal.quantity);
    }
  }, [meal.quantity, meal.members]);

  return (
    <div className='w-full'>
      {usersExceedMeals && (
        <div className='w-full flex justify-between items-center bg-[#FFF4E5] text-[#2E3031] py-3 px-6'>
          <div className='flex gap-4 text-[#FF9500] items-center'>
            <InfoIcon className='w-[18px] h-[18px]' />
            <p className='text-xs'>The number of meals is less than the recipients.</p>
          </div>
          <CloseOutlineIcon className='cursor-pointer' onClick={() => setUsersExceedMeals(false)} />
        </div>
      )}
      <li className='flex items-start gap-4 py-4 border-t-[0.8px] border-t-[#EAEBEB] meal-item px-7'>
        <Dropdown
          visible={showQuantityDropdown}
          onClickOutside={() => setShowQuantityDropdown(false)}
          content={
            <div
              className='flex items-center px-2 py-[1.5px] rounded-[20px] bg-[#F2F2F3] cursor-pointer'
              onClick={() => setShowQuantityDropdown(true)}>
              <p className='text-xs text-red-5'>{meal.quantity}</p>
              <ArrowDownIcon />
            </div>
          }>
          <div className='flex items-center justify-between gap-1 bg-white shadow-lg rounded-lg p-3 text-gray-10'>
            {type === 'bulk' && (
              <IconButton
                small
                icon={
                  <MinusIcon
                    onClick={() =>
                      BulkOrderService.UpdateMealQuantity(
                        'subtract',
                        meal,
                        bulkOrderMeals,
                        setBulkOrderMeals
                      )
                    }
                    className='icon'
                  />
                }
              />
            )}
            {type === ENUM_ORDERS_TYPE.BATCH && (
              <IconButton
                small
                icon={
                  <MinusIcon
                    onClick={() =>
                      BulkOrderService.UpdateMealQuantity(
                        'subtract',
                        meal,
                        batchOrderMeals,
                        setBatchOrderMeals
                      )
                    }
                    className='icon'
                  />
                }
              />
            )}
            <p className='min-w-[16px] flex items-center justify-center text-sm font-semibold'>
              {meal.quantity}
            </p>
            {type === ENUM_ORDERS_TYPE.BULK && (
              <IconButton
                small
                icon={
                  <PlusIcon
                    onClick={() =>
                      BulkOrderService.UpdateMealQuantity(
                        'add',
                        meal,
                        bulkOrderMeals,
                        setBulkOrderMeals
                      )
                    }
                    className='icon'
                  />
                }
              />
            )}
            {type === ENUM_ORDERS_TYPE.BATCH && (
              <IconButton
                small
                icon={
                  <PlusIcon
                    onClick={() =>
                      BulkOrderService.UpdateMealQuantity(
                        'add',
                        meal,
                        batchOrderMeals,
                        setBatchOrderMeals
                      )
                    }
                    className='icon'
                  />
                }
              />
            )}
          </div>
        </Dropdown>
        <article className='flex flex-col gap-[3px] cursor-pointer' onClick={onClick}>
          <p className='text-sm font-medium leading-[21px]'>{meal.name}</p>
          <ul>
            {meal?.addons?.map((addon) => (
              <li key={addon.id} className='text-sm text-[#6F7376] leading-[22.4px]'>
                {addon.quantity}x&nbsp;{addon.name || addon?.meal_data?.name}
              </li>
            ))}
          </ul>
        </article>

        <div className='flex items-center gap-[10px] ml-auto'>
          <p>{formatCurrency(price)}</p>
          {(type === ENUM_ORDERS_TYPE.BULK || type === ENUM_ORDERS_TYPE.BATCH) && (
            <span className='w-5 h-5 flex items-center justify-center cursor-pointer'>
              <TrashIcon onClick={() => removeMeal?.(meal)} />
            </span>
          )}
        </div>
      </li>
      {type === ENUM_ORDERS_TYPE.BULK && (
        <div
          className='flex gap-8 pb-4 px-7 cursor-pointer items-center'
          onClick={() => setShowAssignMealModal(true)}>
          <Users2Icon />
          <p className='text-sm text-[#6F7376] leading-[22.4px] capitalize'>
            {BulkOrderService.GetAssignedUsers(meal)}
          </p>
        </div>
      )}
      {type === ENUM_ORDERS_TYPE.BATCH && (
        <div
          className='flex gap-8 pb-4 px-7 cursor-pointer items-center'
          onClick={() => setShowAssignMealModal(true)}>
          <Users2Icon />
          <p className='text-sm text-[#6F7376] leading-[22.4px] capitalize'>
            {BulkOrderService.GetAssignedUsers(meal)}
          </p>
        </div>
      )}
      {showAssignMealModal && (
        <AssignMealModal
          show={showAssignMealModal}
          onClose={() => setShowAssignMealModal(false)}
          handleClick={(members) => {
            if (type === ENUM_ORDERS_TYPE.BULK) {
              BulkOrderService.UpdateMealUsers(meal, members, bulkOrderMeals, setBulkOrderMeals);
            } else if (type === ENUM_ORDERS_TYPE.BATCH) {
              BulkOrderService.UpdateMealUsers(meal, members, batchOrderMeals, setBatchOrderMeals);
            }
          }}
          defaultMembers={meal.members ?? []}
        />
      )}
    </div>
  );
};

export default MealItem;
